import {
  createEffect,
  createSignal,
  For,
  JSXElement,
  Match,
  Show,
  Switch,
  untrack
} from 'solid-js';
import { Localized } from '~/i18n';

import { ModalSizes } from '~/components/ui/Modal/Modal.tsx';
import Modal from '~/components/ui/Modal';
import {
  CUSTOMIZATION_ADDON_FEATURE_TRANSLATION_KEYS,
  CUSTOMIZATION_ADDON_NAME,
  CUSTOMIZATION_PRO_ADDON_ID,
  getCustomizationAddonPrice,
  getTranslationKey
} from '~/pages/Billing/utils.ts';
import { Tag } from '~/components/ui/Tag';
import { Button } from '~/components/ui/Button';
import { useWorkspaceBillingContext } from '~/pages/Billing/contexts/WorkspaceBillingContext.tsx';
import { useToast } from '~/components/ui/Toast';
import { getLocalizedString } from '~/i18n/utils.ts';
import { useIntl } from '@cookbook/solid-intl';
import {
  addProductToSubscription,
  createCheckoutSession,
  getBillingInfoQueryKey
} from '~/api';
import { initStripe } from '~/utils/stripe.ts';
import { waitForElement } from '~/utils/commonUtils.ts';
import logger from '~/utils/logger';
import { XIcon } from 'lucide-solid';
import SuccessAnimated from '~/assets/success_animated.gif';
import SkLoader from '~/components/ui/SkLoader';
import { useQueryClient } from '@tanstack/solid-query';

enum CustomizationAddonModalSteps {
  ADDON_SUMMARY = 'ADDON_SUMMARY',
  PAYMENT_SETUP = 'PAYMENT_SETUP',
  ADDON_PURCHASE_SUCCESS = 'ADDON_PURCHASE_SUCCESS'
}

const CHECKOUT_CONTAINER_ID = 'checkout-container';

export default function UpgradeCustomizationAddonModal(): JSXElement {
  const workspaceBillingContext = useWorkspaceBillingContext();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const toaster = useToast();
  const [isCtaLoading, setIsCtaLoading] = createSignal(false);
  const [isDataLoading, setIsDataLoading] = createSignal(true);
  const [isPaymentMethodPresent, setIsPaymentMethodPresent] =
    createSignal(false);

  let checkout: any = null;

  const [currentStep, setCurrentStep] =
    createSignal<CustomizationAddonModalSteps>(
      CustomizationAddonModalSteps.ADDON_SUMMARY
    );

  const fetchClientSecret = async () => {
    try {
      const { clientSecret } = await createCheckoutSession();
      if (!clientSecret) {
        throw new Error('Client secret not found in response');
      }
      return clientSecret;
    } catch (err) {
      logger.error(`Failed to fetch client secret for embedded checkout`, err);
      return '';
    }
  };

  const initializeEmbeddedCheckout = async () => {
    const stripe = await initStripe(
      workspaceBillingContext.getPublishableToken(),
      async () => {
        workspaceBillingContext.hideCustomizationUpgradeModal();
        toaster.toast().error({
          description: getLocalizedString(
            getTranslationKey(`customization_add_upgrade_failure_toast`),
            { intl }
          )
        });
      }
    );

    if (stripe) {
      await waitForElement(`#${CHECKOUT_CONTAINER_ID}`);
      if (checkout) {
        checkout.destroy();
        document.getElementById(CHECKOUT_CONTAINER_ID)!.innerHTML = '';
      }
      checkout = await stripe.initEmbeddedCheckout({
        fetchClientSecret,
        onComplete: async () => {
          await addAddonToSubscription();
          setCurrentStep(CustomizationAddonModalSteps.ADDON_PURCHASE_SUCCESS);
        }
      });
      checkout.mount(`#${CHECKOUT_CONTAINER_ID}`);
    }
  };

  createEffect(() => {
    if (!workspaceBillingContext.shouldShowCustomizationUpgradeModal) {
      untrack(() => {
        setIsCtaLoading(false);
        setTimeout(() => {
          // to avoid the transition to be visible
          // when the modal closing animation is inprogress
          setCurrentStep(CustomizationAddonModalSteps.ADDON_SUMMARY);
          setIsDataLoading(true);
        }, 500);
      });
    } else {
      untrack(async () => {
        setIsDataLoading(true);
        setIsPaymentMethodPresent(
          await workspaceBillingContext.isPaymentMethodPresent()
        );
        setIsDataLoading(false);
      });
    }
  });

  const addAddonToSubscription = async () => {
    try {
      setIsCtaLoading(true);
      await addProductToSubscription({
        productName: CUSTOMIZATION_PRO_ADDON_ID
      });
      await workspaceBillingContext.refetchBillingInfo?.();
      await queryClient.invalidateQueries({
        queryKey: getBillingInfoQueryKey(),
        exact: true,
        type: 'all'
      });
    } catch (e) {
      toaster.toast().error({
        description: getLocalizedString(
          getTranslationKey(`customization_add_upgrade_failure_toast`),
          { intl }
        )
      });
    } finally {
      setIsCtaLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div class={`flex items-center`}>
          <Localized
            translationKey={getTranslationKey(
              'customization_addon_modal_header'
            )}
            vars={{
              customizationAddonName: CUSTOMIZATION_ADDON_NAME
            }}
          />
          <Tag class={`text-sm font-medium`}>
            <Localized
              translationKey={getTranslationKey(`customization_addon_tag`)}
            />
          </Tag>
          <Show
            when={currentStep() === CustomizationAddonModalSteps.ADDON_SUMMARY}
            fallback={
              <Button
                size={`md`}
                variant={`ghost`}
                onClick={() => {
                  workspaceBillingContext.hideCustomizationUpgradeModal();
                }}
                class={`ml-auto`}
              >
                <XIcon />
              </Button>
            }
          >
            <span class={`ml-auto text-sm font-medium`}>
              {getCustomizationAddonPrice()}
            </span>
          </Show>
        </div>
      }
      open={workspaceBillingContext.shouldShowCustomizationUpgradeModal}
      size={ModalSizes.LARGE}
    >
      <Show
        when={!isDataLoading()}
        fallback={
          <div class={`relative min-h-[360px]`}>
            <SkLoader />
          </div>
        }
      >
        <Show
          when={currentStep() === CustomizationAddonModalSteps.PAYMENT_SETUP}
        >
          <div
            id={CHECKOUT_CONTAINER_ID}
            class={`min-h-[360px]`}
          ></div>
        </Show>
        <Show
          when={currentStep() === CustomizationAddonModalSteps.ADDON_SUMMARY}
        >
          <div class={`mt-4 text-md`}>
            <Localized
              translationKey={getTranslationKey(
                `customization_addon_description_in_modal`
              )}
              vars={{
                customizationAddonName: CUSTOMIZATION_ADDON_NAME
              }}
            />
          </div>
          <div class={`mt-6`}>
            <h6 class={`text-sm text-fg-default`}>
              <Localized
                translationKey={getTranslationKey(
                  `customization_addon_features_title`
                )}
              />
            </h6>
            <ul class={`mt-2.5 flex flex-wrap items-center gap-2`}>
              <For each={CUSTOMIZATION_ADDON_FEATURE_TRANSLATION_KEYS}>
                {(key) => (
                  <li>
                    <Tag
                      class={`mx-0 py-2 px-3 rounded-3xl text-gray-700 font-medium border border-[#BECEF4]`}
                    >
                      <Localized translationKey={getTranslationKey(key)} />
                    </Tag>
                  </li>
                )}
              </For>
            </ul>
          </div>
        </Show>
        <Show
          when={currentStep() === CustomizationAddonModalSteps.ADDON_SUMMARY}
        >
          <div class={`w-full mt-10 flex justify-end gap-3`}>
            <Button
              variant={`outline`}
              size={`sm`}
              onClick={() => {
                workspaceBillingContext.hideCustomizationUpgradeModal();
              }}
            >
              <Localized translationKey={`cta_labels.cancel`} />
            </Button>
            <Button
              size={`sm`}
              loading={isCtaLoading()}
              onClick={async () => {
                if (
                  !isPaymentMethodPresent() &&
                  currentStep() === CustomizationAddonModalSteps.ADDON_SUMMARY
                ) {
                  setCurrentStep(CustomizationAddonModalSteps.PAYMENT_SETUP);
                  setTimeout(async () => {
                    await initializeEmbeddedCheckout();
                  });
                  return;
                }
                await addAddonToSubscription();
                setCurrentStep(
                  CustomizationAddonModalSteps.ADDON_PURCHASE_SUCCESS
                );
              }}
            >
              <Switch>
                <Match
                  when={
                    currentStep() ===
                      CustomizationAddonModalSteps.ADDON_SUMMARY &&
                    isPaymentMethodPresent()
                  }
                >
                  <Localized
                    translationKey={getTranslationKey(
                      `modal_cta_purchase_addon`
                    )}
                  />
                </Match>
                <Match
                  when={
                    currentStep() ===
                      CustomizationAddonModalSteps.ADDON_SUMMARY &&
                    !isPaymentMethodPresent()
                  }
                >
                  <Localized
                    translationKey={getTranslationKey(
                      `modal_cta_purchase_addon_add_payment_method`
                    )}
                  />
                </Match>
              </Switch>
            </Button>
          </div>
        </Show>
        <Show
          when={
            currentStep() ===
            CustomizationAddonModalSteps.ADDON_PURCHASE_SUCCESS
          }
        >
          <div class={`h-[100px]`}>
            <img
              class={`max-h-full mx-auto`}
              src={SuccessAnimated}
              alt={`Success Animation`}
            />
          </div>
          <div class={`mt-6 text-center font-medium`}>
            <Localized
              translationKey={getTranslationKey(
                `modal_purchase_success_message`
              )}
            />
          </div>
        </Show>
      </Show>
    </Modal>
  );
}
